<script setup lang="ts">
import { i18n } from '@/i18n';
import ControlSupplierPageTab from '../components/ControlSupplierPageTab.vue';
import { SUPPLIER_TAB_ROUTER_INFO } from '../constants';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import { useAllBusinessesForSupplierForecastStore } from './useAllBusinessesForSupplierForecastStore';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onMounted } from 'vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import SelectInput from '@/corePagesModule/SelectInput';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import EditForecastModal from '../components/EditForecastModal.vue';
import DownloadForecastModal from '../components/DownloadForecastModal.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';

const t = i18n.t;
const locale = computed(() => i18n.locale);
const store = useAllBusinessesForSupplierForecastStore();
const { state } = storeToRefs(store);

onBeforeMount(() => {
  store.init();
});

const monthsOptionsDisplayForecast = computed(() => {
  return store.monthsOptionsDisplayForecastNumber.map((monthsOption) => monthsOption + t('unitMonth'));
});
</script>

<template>
  <div class="all-businesses">
    <!-- フィルターモーダル -->
    <FilterModalButton
      :filter-modal-type="'supplierForecast'"
      :select-input-index="1"
      :filter-items="store.getFilterItemsValue().value"
      @apply="(filteredContents) => store.onAppliedFilterModal(filteredContents)"
    />
    <div class="all-businesses__top-area top-area">
      <!-- 20250910 ダッシュボード未実装のためOmit -->
      <!--<ControlSupplierPageTab class="top-area__tab-bar" :tab-info-object="[...SUPPLIER_TAB_ROUTER_INFO]" />-->
      <div class="top-area__button-area button-area">
        <TextButton
          class="button-area__edit-button edit-button button-area__button button-area__button--fill btn-primary"
          :disabled="store.isDisabledEditButton"
          @click="store.onClickEditButton"
        >
          <div class="edit-button__container">
            <p class="edit-button__text">{{ t('担当者編集') }}</p>
            <SvgLoader class="edit-button__icon" name="editPen" />
          </div>
        </TextButton>
        <EditForecastModal
          v-if="state.editForecastModalState.modalState"
          class="button-area__edit-button"
          :store="store"
          :index-of-select-input="21"
        />
        <TextButton
          class="button-area__download-button download-button button-area__button button-area__button--white btn-primary"
          :disabled="store.isDisabledOpenDownloadModal"
          @click="store.onClickDownloadButton"
        >
          <div class="download-button__container">
            <p class="download-button__text">{{ t('全てダウンロード') }}</p>
            <SvgLoader class="download-button__icon" name="fatArrowDown" />
          </div>
        </TextButton>
        <DownloadForecastModal
          v-if="state.downloadForecastModalState.modalState"
          class="button-area__download-button"
          :store="store"
          :index-of-select-input="22"
        />
      </div>
    </div>
    <div class="all-businesses__table-area table-area">
      <div class="table-area__top-accessory">
        <div class="table-area__select-month-area select-month-area">
          <div class="select-month-area__title">
            {{ t('内示作成月') }}
          </div>
          <MonthPicker
            class="select-month-area__calendar"
            :model-value="state.yearMonthInfoCreatedForecast"
            :locale="locale"
            :prop-format="t('MonthPickerFormat')"
            @update:model-value="store.onSelectedCreatedMonth($event)"
          />
        </div>
        <Pagination
          v-if="state.businessTableContents.pagingOptions"
          :paging-options="state.businessTableContents.pagingOptions"
          :is-loading="state.businessTableContents.loadingState.status.type === 'loading'"
          class="table-area__pagination"
          @on-change-page="store.onChangePaging($event)"
        />
        <!-- TODO: 24/05/28 合計を表示/非表示のセレクトインプットを追加する。6月開発段階ではなし -->
        <SelectInput
          class="table-area__forecast-length-select"
          :index="23"
          :model-value="t('displayMonthsOfData', { month: state.monthsDisplayForecast })"
          :placeholder="''"
          :label="''"
          :filter-type="'DISPLAY_MONTH'"
          :prop-options="monthsOptionsDisplayForecast"
          :is-open-prop="state.isOpenSelectInputType === 'DISPLAY_MONTH'"
          use-teleport
          @click:select-input="() => (state.isOpenSelectInputType = 'DISPLAY_MONTH')"
          @update:value-with-index="(_, index) => store.onSelectedMonthsDisplayForecast(index)"
        />
      </div>
      <div
        v-if="state.businessTableContents.loadingState.status.type === 'zeroMatch'"
        class="table-area__business-table table-area__business-table--zero-match"
      >
        {{ t('取引が存在しません') }}
      </div>
      <NewBaseTableContent
        v-else
        :key="state.businessTableContents.renderingKey"
        class="table-area__business-table table-area__business-table--success"
        :prop-table-content-properties="state.businessTableContents.contentHeader"
        :prop-display-objects="state.businessTableContents.contentItems"
        :prop-loading-status="state.businessTableContents.loadingState"
        :prop-table-type="'supplierForecastAllBusinessTable'"
        @checked="state.businessTableContents.checkedDisplayIds = $event"
        @on-click-sort="(sortKey) => store.sortTableContentItems(sortKey)"
        @on-click-filter="(narrowState) => store.filterTableContentItems(narrowState)"
      />
    </div>
  </div>
  <!-- 仕入先内示ダウンロード時のローディングモーダル -->
  <InfoModal :modal-state="isLoading(state.downloadForecastModalState.loadingState.status)" icon="loading" :enable-outside-click="false">
    <template #title>{{ t('仕入先内示をダウンロード中です。') }}</template>
  </InfoModal>
</template>

<style lang="scss" scoped>
.all-businesses {
  width: 100%;
  min-height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto) minmax(0, 1fr);
  gap: 8px;
  font: Noto Sans JP;
  letter-spacing: 0;
  white-space: nowrap;

  .top-area {
    display: grid;
    grid-template-columns: repeat(2, auto);
    align-items: start;
    justify-content: start;
    gap: 16px;

    .button-area {
      display: grid;
      grid-template-columns: repeat(3, auto);
      gap: 16px;

      &__button {
        font-weight: 700;
        border-radius: 4px;

        &--fill {
          font-size: 14px;
          background-color: $semanticPrimary;
          color: $semanticWhite;
        }

        &--white {
          font-size: 16px;
          line-height: 22px;
          background-color: $semanticWhite;
          border: 1px solid $semanticPrimary;
          color: $semanticPrimary;
        }
      }
    }
  }

  .select-month-area {
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: start;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;

    &__title {
      font-size: 14px;
      line-height: 20.27px;
      font-weight: 400;
      color: $textDefault;
    }

    &__calendar {
      width: 142px;
      height: 36px;
      background-color: $semanticWhite;
      border-radius: 4px;
      border: 1px solid $borderDefault;
      display: grid;
      place-items: center;
    }
  }

  .table-area {
    height: 100%;
    overflow-x: auto;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);

    &__top-accessory {
      display: grid;
      grid-template-columns: repeat(3, auto);
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }

    &__business-table {
      &--zero-match {
        background-color: $surfaceDefault;
        display: grid;
        place-items: center;
        font-weight: 400;
        font-size: 14px;
        line-height: 18.2px;
        color: $textSecondary;
      }
      &--success {
        overflow-y: scroll !important;
        background-color: $backgroundDefault;
      }
      border: 1px solid $borderDefault;
      min-height: 0 !important;
      max-height: 100% !important;
    }

    &__forecast-length-select {
      width: 162px;
      height: 32px;
    }
  }
}

.all-businesses ::v-deep .select-filter-button {
  width: fit-content;
  justify-self: start;
}

.edit-button {
  width: 156px;
  height: 34px;
  padding: 0 17px;
  display: grid;
  place-items: center;

  &__container {
    display: grid;
    grid-template-columns: auto 22px;
    place-items: center;
    gap: 4px;
  }

  &__icon {
    color: white;
  }
}

.download-button {
  height: 34px;
  padding: 0 16px;
  display: grid;
  place-items: center;

  &__container {
    display: grid;
    grid-template-columns: auto 22px;
    place-items: center;
    gap: 4px;
  }
}
</style>

<style lang="scss">
.select-month-area__calendar input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
}
</style>
